export interface RuntimeCfg {
  REACT_APP_API_URL: string;
  REACT_APP_API_PAYMENT_URL: string;
  REACT_APP_API_ADS_URL: string;
  REACT_APP_API_NEGOTIATE_URL: string;
  REACT_APP_API_RTA_URL: string;
  REACT_APP_API_RTA_SOCKET_PATH: string;
  REACT_APP_API_MEDIA_COMPRESSOR: string;
  REACT_APP_API_NOTIFY_URL: string;
  REACT_APP_API_SUPPORT_URL: string;
  REACT_APP_NEGOTIATE_MOCK_MODE?: string;
  REACT_APP_VERIFICATION_PROVIDER_HOSTS?: string;
  /**
   * Terms version the company accepts when it authorizes the recurrence. It
   * must equal Payments' EFI_PIX_TERMS_VERSION, which rejects any other value.
   */
  REACT_APP_PIX_AUTOMATICO_TERMS_VERSION: string;
}

function loadCfg(): RuntimeCfg {
  const runtime = (window as any).__RUNTIME_CONFIG__ as Partial<RuntimeCfg> | undefined;

  const build: Partial<RuntimeCfg> = {
    REACT_APP_API_URL: process.env.REACT_APP_API_URL,
    REACT_APP_API_PAYMENT_URL: process.env.REACT_APP_API_PAYMENT_URL,
    REACT_APP_API_ADS_URL: process.env.REACT_APP_API_ADS_URL,
    REACT_APP_API_NEGOTIATE_URL: process.env.REACT_APP_API_NEGOTIATE_URL,
    REACT_APP_API_RTA_URL: process.env.REACT_APP_API_RTA_URL,
    REACT_APP_API_RTA_SOCKET_PATH: process.env.REACT_APP_API_RTA_SOCKET_PATH,
    REACT_APP_API_MEDIA_COMPRESSOR: process.env.REACT_APP_API_MEDIA_COMPRESSOR,
    REACT_APP_API_NOTIFY_URL: process.env.REACT_APP_API_NOTIFY_URL,
    REACT_APP_API_SUPPORT_URL: process.env.REACT_APP_API_SUPPORT_URL,
    REACT_APP_NEGOTIATE_MOCK_MODE: process.env.REACT_APP_NEGOTIATE_MOCK_MODE,
    REACT_APP_VERIFICATION_PROVIDER_HOSTS: process.env.REACT_APP_VERIFICATION_PROVIDER_HOSTS,
    REACT_APP_PIX_AUTOMATICO_TERMS_VERSION: process.env.REACT_APP_PIX_AUTOMATICO_TERMS_VERSION,
  };

  const merged = { ...build, ...runtime };

  if (!merged.REACT_APP_API_URL) {
    throw new Error("REACT_APP_API_URL is not defined");
  }
  if (!merged.REACT_APP_API_PAYMENT_URL) {
    throw new Error("REACT_APP_API_PAYMENT_URL is not defined");
  }
  if (!merged.REACT_APP_API_ADS_URL) {
    throw new Error("REACT_APP_API_ADS_URL is not defined");
  }
  if (!merged.REACT_APP_API_NEGOTIATE_URL) {
    throw new Error("REACT_APP_API_NEGOTIATE_URL is not defined");
  }
  if (!merged.REACT_APP_API_RTA_URL) {
    throw new Error("REACT_APP_API_RTA_URL is not defined");
  }
  if (!merged.REACT_APP_API_RTA_SOCKET_PATH) {
    throw new Error("REACT_APP_API_RTA_SOCKET_PATH is not defined");
  }
  if (!merged.REACT_APP_API_MEDIA_COMPRESSOR) {
    throw new Error("REACT_APP_API_MEDIA_COMPRESSOR is not defined");
  }
  if (!merged.REACT_APP_API_NOTIFY_URL) {
    throw new Error("REACT_APP_API_NOTIFY_URL is not defined");
  }
  if (!merged.REACT_APP_API_SUPPORT_URL) {
    throw new Error("REACT_APP_API_SUPPORT_URL is not defined");
  }

  // Support is served by tartaro-operations, fronted by the enterprise Ingress
  // at the sibling path /support. It is NOT part of dakasa-enterprise-api, so
  // its base URL must never be nested under REACT_APP_API_URL (the /enterprise
  // base). A value like ".../enterprise/support" reaches enterprise-api, which
  // has no /support routes, so every customer support read (GET /tickets, the
  // thread, the reply) 404s. The create still succeeds on /enterprise/support-ticket,
  // so a ticket is enqueued and persisted in tartaro-operations, but the inbox
  // can never read it back and it looks like the ticket never saved. Fail loud
  // here at config load instead of shipping that silent 404.
  if (
    merged.REACT_APP_API_SUPPORT_URL === merged.REACT_APP_API_URL ||
    merged.REACT_APP_API_SUPPORT_URL.startsWith(merged.REACT_APP_API_URL + "/")
  ) {
    throw new Error(
      `REACT_APP_API_SUPPORT_URL (${merged.REACT_APP_API_SUPPORT_URL}) must not be nested under ` +
        `REACT_APP_API_URL (${merged.REACT_APP_API_URL}). Support is served by tartaro-operations at ` +
        `the sibling /support route, not by enterprise-api. Point it at the /support host/path (relative ` +
        `"/support" behind the Ingress, or the tartaro-operations host in dev).`,
    );
  }

  // Payments rejects an enrollment whose termsVersion differs from its own, so
  // the Pix-only payment screen must fail at startup without an explicit
  // version instead of offering an authorization that can only fail.
  if (!merged.REACT_APP_PIX_AUTOMATICO_TERMS_VERSION?.trim()) {
    throw new Error(
      "REACT_APP_PIX_AUTOMATICO_TERMS_VERSION is required. It must match Payments " +
        "EFI_PIX_TERMS_VERSION.",
    );
  }

  return merged as RuntimeCfg;
}

export const cfg = loadCfg();
